import type { ReactNode } from 'react';
import { cx } from '../cx';
import type { ChartLayout } from './layout';

export type WaffleProps = {
  /** The share, 0 to 100; rounded to whole squares. */
  percent: number;
  /** The figure written beside the grid, formatted by the page ("26 in 100"). */
  figure: ReactNode;
  /** What the figure counts ("open jobs state their pay"). */
  caption: ReactNode;
  /** One sentence for screen readers ("26 of every 100 open jobs state their pay."). */
  description: string;
  /** `auto` (default) switches to the narrow form in a narrow container: the figure and caption sit above the grid, which takes the full width. */
  layout?: ChartLayout;
  className?: string;
};

/**
 * One share as 100 squares, filled row by row from the top left: a figure people can count. For
 * headline shares; comparing shares across categories is SplitRows.
 */
const ROOT: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-3 @md:flex-row @md:flex-wrap @md:items-center @md:gap-5',
  narrow: 'flex flex-col gap-3',
  wide: 'flex flex-wrap items-center gap-5',
};
const GRID: Record<ChartLayout, string> = { auto: 'max-w-96 @md:max-w-80', narrow: 'max-w-96', wide: 'max-w-80' };
const TEXT: Record<ChartLayout, string> = { auto: '@max-md:order-first', narrow: 'order-first', wide: '' };

export function Waffle({ percent, figure, caption, layout = 'auto', description, className }: WaffleProps) {
  const filled = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div className={cx('@container', ROOT[layout], className)}>
      <div role="img" aria-label={description} className={cx('grid w-full grid-cols-20 gap-0.5', GRID[layout])}>
        {Array.from({ length: 100 }, (_, i) => (
          <span
            key={i}
            className={cx('aspect-square rounded-xs', i < filled ? 'bg-jc-chart-2' : 'bg-jc-chart-context')}
          />
        ))}
      </div>
      <p aria-hidden="true" className={cx('flex flex-col type-meta text-jc-ink-secondary', TEXT[layout])}>
        <span className="type-figure-lg text-jc-ink">{figure}</span>
        {caption}
      </p>
    </div>
  );
}
